import { useState, useEffect, useRef } from "react";
import { db, storage, auth } from "./firebase.js";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";
import { ref, getDownloadURL, uploadBytesResumable } from "firebase/storage";

export default function ProfileMenu() {
  const [turoId, setTuroId] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [csvFile, setCsvFile] = useState(null); // ✅ start null, not "nonnull"
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [user, setUser] = useState(null);
  const [isPro, setIsPro] = useState(false);
  const [isCohost, setIsCohost] = useState(false);
  const [cohostError, setCohostError] = useState("");
  const [cohostStatus, setCohostStatus] = useState("");
  const [isVerified, setIsVerified] = useState(true);
  const [cohostDescription, setCohostDescription] = useState("");
  const [uploadPct, setUploadPct] = useState(0);
  const [goldenMeta, setGoldenMeta] = useState(null);

  // Keep a stable mirror of the file to survive re-renders
  const fileRefStable = useRef(null);

  // Ensure we only prefill once
  const prefillDone = useRef(false);

  /* ----------------------- Golden meta live timestamp ----------------------- */
  useEffect(() => {
    if (!user) return;
    const metaRef = doc(db, `users/${user.uid}/golden/meta`);
    const unsub = onSnapshot(metaRef, (snap) => {
      if (snap.exists()) setGoldenMeta(snap.data());
    });
    return () => unsub();
  }, [user]);

  /* ------------------------------ Auth / Prefill ---------------------------- */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      setUser(u);
      if (!u) return;

      try {
        const userRef = doc(db, "users", u.uid);
        const snap = await getDoc(userRef);

        if (snap.exists()) {
          const data = snap.data();

          // Prefill only once, do not overwrite user-typed values
          if (!prefillDone.current) {
            if (data.turo_user_id) {
              setTuroId((prev) => (prev ? prev : String(data.turo_user_id)));
            }
            if (data.zipcode) {
              setZipCode((prev) => (prev ? prev : String(data.zipcode)));
            }
            prefillDone.current = true;
          }

          setIsPro(!!data.isPro);
          setIsVerified(!!data.report_csv);
          setIsCohost(!!data.isCohost);
          setCohostDescription(data.cohost_description || "");
        }
      } catch (err) {
        console.error("Error checking user info:", err);
        setError("An error occurred while checking your profile.");
      }
    });

    return () => unsub(); // cleanup listener
  }, []);

  /* --------------------------------- Cohost -------------------------------- */
  const handleCohostSubmit = async () => {
    if (!user) return;
    setCohostStatus("Saving...");
    setCohostError("");

    const trimmedId = (turoId || "").trim();
    if (!trimmedId) {
      setCohostError("Turo ID cannot be empty.");
      setCohostStatus("");
      return;
    }

    try {
      const snap = await getDocs(
        query(collection(db, "users"), where("turo_user_id", "==", trimmedId))
      );

      // Only block if any doc with this Turo ID is NOT the current user
      const claimedByOther = snap.docs.some((d) => d.id !== user.uid);
      if (claimedByOther) {
        setCohostError("That Turo ID is already claimed by another user.");
        setCohostStatus("");
        return;
      }

      const userRef = doc(db, "users", user.uid);
      await setDoc(
        userRef,
        {
          isCohost: true,
          turo_user_id: trimmedId,
          cohost_description: cohostDescription,
        },
        { merge: true }
      );

      setCohostStatus("✅ Saved successfully.");
    } catch (err) {
      console.error("Cohost submission error:", err);
      setCohostError("An error occurred while saving.");
      setCohostStatus("");
    }
  };

  /* --------------------------------- Upload -------------------------------- */
  const isValidZip = (zip) => /^\d{5}(-\d{4})?$/.test(String(zip || ""));

  const handleFilePick = (e) => {
    const f = e.target.files?.[0] ?? null;
    setStatus("");
    setError("");
    setUploadPct(0);

    if (!f) {
      setCsvFile(null);
      fileRefStable.current = null;
      setError("No file selected.");
      return;
    }

    const looksCsv =
      f.type === "text/csv" ||
      f.type === "application/vnd.ms-excel" || // some browsers use this for CSV
      /\.csv$/i.test(f.name);

    if (!looksCsv) {
      setCsvFile(null);
      fileRefStable.current = null;
      setError("Please select a .csv file exported from Turo.");
      return;
    }

    if (f.size < 200) {
      setCsvFile(null);
      fileRefStable.current = null;
      setError("CSV seems too small—please re-export from Turo.");
      return;
    }

    setCsvFile(f);
    fileRefStable.current = f;
  };

  const handleSubmit = async () => {
    if (!user) return;

    const trimmedId = (turoId || "").trim();
    if (!trimmedId) {
      setError("Turo ID cannot be empty.");
      return;
    }
    if (!(fileRefStable.current instanceof File)) {
      setError("Please choose your Turo earnings .csv first.");
      return;
    }
    if (!isValidZip(zipCode)) {
      setError("Enter a valid 5-digit ZIP code.");
      return;
    }

    const file = fileRefStable.current;

    setError("");
    setStatus("Uploading…");
    setUploadPct(0);

    try {
      // Enforce unique Turo ID (allow if it's this user)
      const snap = await getDocs(
        query(collection(db, "users"), where("turo_user_id", "==", trimmedId))
      );
      const claimedByOther = snap.docs.some((d) => d.id !== user.uid);
      if (claimedByOther) {
        setError("That Turo ID is already claimed by another user.");
        setStatus("");
        return;
      }

      // Safe filename, keep .csv
      const safeName = String(file.name || "report.csv")
        .replace(/[^\w.\-]+/g, "_")
        .slice(-120);
      const path = `reports/${user.uid}/${safeName}`;

      // Resumable upload with progress + proper contentType
      const storageRef = ref(storage, path);
      const task = uploadBytesResumable(storageRef, file, {
        contentType: "text/csv; charset=utf-8",
      });

      await new Promise((resolve, reject) => {
        task.on(
          "state_changed",
          (s) => {
            const pct = Math.round(
              (s.bytesTransferred / Math.max(s.totalBytes, 1)) * 100
            );
            setUploadPct(pct);
            setStatus(`Uploading… ${pct}%`);
          },
          (err) => reject(err),
          () => resolve()
        );
      });

      const fileUrl = await getDownloadURL(storageRef);

      // Update user doc
      const userRef = doc(db, "users", user.uid);

      await setDoc(
        userRef,
        {
          turo_user_id: trimmedId,
          zipcode: String(zipCode || ""),
          report_csv: fileUrl, // optional, golden is canonical
        },
        { merge: true }
      );

      setIsVerified(true);
      setStatus("Uploaded! Building your insights…");
      setError("");

      // Clear local file after success (optional)
      setCsvFile(null);
      fileRefStable.current = null;
      setUploadPct(0);
    } catch (err) {
      console.error("Upload failed:", err);
      setError(
        "Upload failed. Please re-check your CSV and try again (or re-export from Turo)."
      );
      setStatus("");
      setUploadPct(0);
    }
  };

  const handleToggleCohost = async (newValue) => {
    if (!user) return;
    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, { isCohost: newValue });
      setIsCohost(newValue);
      console.log("Cohost status updated:", newValue);
    } catch (error) {
      console.error("Error updating isCohost:", error);
    }
  };

  function CoHostToggle({ value, onChange }) {
    return (
      <div className="flex items-center justify-between mb-4">
        <label className="text-sm font-medium text-gray-700">
          Open to co-host cars? We'll showcase your profile under "Find Cohosts"
        </label>
        <button
          type="button"
          onClick={() => onChange(!value)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
            value ? "bg-green-500" : "bg-gray-300"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
              value ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>
    );
  }

  const getNextDayDate = () => {
    const now = new Date();
    now.setDate(now.getDate() + 1);
    return now.toISOString();
  };

  const isSubmitDisabled =
    !(csvFile instanceof File) || !isValidZip(zipCode) || !turoId;

  return (
    <div className="space-y-4 max-w-7xl mx-auto px-4">
      <div className="space-y-2 p-4 max-w-md mx-auto bg-white shadow rounded-lg">
        <h2 className="text-xl font-semibold mb-2">
          {isVerified
            ? "Update your business insights data"
            : "⭐ Claim Your Turo Profile"}
        </h2>

        <p className="text-sm text-gray-600 mb-4">
          Enter your Turo ID and upload a{" "}
          <a
            href="https://turo.com/us/en/business/earnings"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline hover:text-blue-800"
          >
            trip earnings CSV
          </a>{" "}
          to <strong>generate business insights</strong>.
        </p>

        <p className="text-xs text-gray-500 mt-1">Enter ID numbers only.</p>
        <input
          type="text"
          placeholder="turo.com/us/en/drivers/[YOUR TURO ID]"
          value={turoId}
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d*$/.test(value)) setTuroId(value);
          }}
          className="w-full border px-3 py-2 rounded mb-3"
          inputMode="numeric"
          pattern="[0-9]*"
        />

        <input
          type="text"
          placeholder="Zip code where you host your rentals"
          value={zipCode}
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d{0,5}$/.test(value)) setZipCode(value);
          }}
          className="w-full border px-3 py-2 rounded mb-3"
          inputMode="numeric"
          pattern="\d{5}"
          maxLength={5}
        />

        <a
          href="https://turo.com/us/en/business/earnings"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src="/csv_instructions.png"
            alt="CSV Instructions"
            className="w-full max-w-4xl mx-auto rounded-lg shadow-lg"
          />
        </a>

        {/* File picker with strong validation */}
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={handleFilePick}
          className="mb-1"
        />
        {csvFile instanceof File && (
          <p className="text-xs text-gray-600 -mt-1 mb-2">
            Selected: <span className="font-medium">{csvFile.name}</span> (
            {Math.max(1, Math.round(csvFile.size / 1024)).toLocaleString()} KB)
          </p>
        )}

        <div className="relative group w-full">
          <button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className={`w-full py-2 rounded transition ${
              !isSubmitDisabled
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {isVerified ? "Update" : "Submit"}
          </button>

          {!!status && (
            <p className="text-sm mt-2">
              {status}{" "}
              {uploadPct > 0 && uploadPct < 100 ? `(${uploadPct}%)` : null}
            </p>
          )}

          {goldenMeta?.lastUpdated && (
            <p className="text-xs text-gray-600 mt-1">
              {`Last updated: ${new Date(
                goldenMeta.lastUpdated
              ).toLocaleString()}`}
            </p>
          )}

          {isSubmitDisabled && (
            <div className="absolute left-1/2 -translate-x-1/2 mt-2 text-sm text-red-500 text-center w-full group-hover:block hidden">
              Please upload a CSV, enter a valid ZIP, and provide your Turo ID.
            </div>
          )}
        </div>

        {!!error && <p className="text-red-500 mt-2">{error}</p>}
        {!!status && !/Uploading/.test(status) && (
          <p className="text-green-600 mt-2">{status}</p>
        )}
      </div>

      {isVerified && (
        <div className="flex flex-col items-center justify-center space-y-2 p-4 max-w-md mx-auto bg-white shadow rounded-lg">
          <h3 className="text-xl font-semibold mb-2">✅ Profile claimed!</h3>
        </div>
      )}

      {/* Cohost block */}
      <div className="flex flex-col justify-center space-y-2 p-4 max-w-md mx-auto bg-white shadow rounded-lg">
        <CoHostToggle value={isCohost} onChange={handleToggleCohost} />

        {isCohost && (
          <div className="mt-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Enter your Turo Profile ID
              </label>
              <input
                type="text"
                value={turoId}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) setTuroId(value);
                }}
                placeholder="turo.com/us/en/drivers/[YOUR TURO ID]"
                inputMode="numeric"
                pattern="[0-9]*"
                className="w-full border px-3 py-2 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Co-host Details{" "}
                <span className="text-xs text-gray-400">
                  (shown to interested co-hostees)
                </span>
              </label>
              <textarea
                value={cohostDescription}
                onChange={(e) => setCohostDescription(e.target.value)}
                rows={4}
                placeholder="Describe your co-hosting availability, area, or contact..."
                className="w-full border px-3 py-2 rounded"
              />
            </div>

            <button
              onClick={handleCohostSubmit}
              className="w-full py-2 rounded bg-blue-600 text-white hover:bg-blue-700 mt-2"
            >
              Save Co-host Details
            </button>

            {!!cohostError && (
              <p className="text-red-500 text-sm mt-1">{cohostError}</p>
            )}
            {!!cohostStatus && (
              <p className="text-green-600 text-sm mt-1">{cohostStatus}</p>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col items-center justify-center space-y-2 p-4 max-w-md mx-auto bg-white shadow rounded-lg">
        <a
          href="https://billing.stripe.com/p/login/9B6eVcaeieFGfXwcEM7g400"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-green-600 hover:underline"
        >
          Manage Billing
        </a>
      </div>
    </div>
  );
}
